import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import api from '../../lib/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setInfo(data);
          setStatus('paid');
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('error');
          return;
        }
      } catch (e) {
        console.warn('Payment status check failed, will retry:', e);
      }
      if (attemptsRef.current >= 10) {
        setStatus('pending');
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-[#f1e3eb] flex items-center justify-center px-4 py-20">
      <Card className="max-w-md w-full border-0 shadow-xl" data-testid="payment-success-card">
        <CardContent className="p-10 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-14 h-14 text-[#5d6b3c] mx-auto mb-6 animate-spin" />
              <h1 className="font-serif text-2xl text-[#3c4a2a] mb-3">Confirming your donation…</h1>
              <p className="text-[#5f6d51]">Please wait while we verify your payment.</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <CheckCircle2 className="w-14 h-14 text-[#5d6b3c] mx-auto mb-6" data-testid="payment-paid-icon" />
              {info?.type === 'membership' ? (
                <>
                  <h1 className="font-serif text-2xl text-[#3c4a2a] mb-3">Welcome to the Club!</h1>
                  <p className="text-[#5f6d51] mb-8" data-testid="membership-success-message">
                    Your <strong>{info.membership_level}</strong> membership is now active.
                    Visit your member portal to explore your benefits. A receipt has been sent to your email.
                  </p>
                  <div className="flex flex-col gap-3">
                    <Link to="/member-dashboard">
                      <Button className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white" data-testid="membership-success-portal-btn">
                        Go to My Portal
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="w-full border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white">
                        Return Home
                      </Button>
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="font-serif text-2xl text-[#3c4a2a] mb-3">Thank You for Your Generosity!</h1>
                  <p className="text-[#5f6d51] mb-8">
                    {info?.type === 'monthly_giving'
                      ? 'Your monthly gift is now active. Your ongoing support empowers women and strengthens Lincoln County, month after month.'
                      : 'Your donation was received successfully. Your support empowers women and strengthens Lincoln County.'}{' '}
                    A receipt has been sent to your email.
                  </p>
                  <div className="flex flex-col gap-3">
                    <Link to="/">
                      <Button className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white" data-testid="payment-success-home-btn">
                        Return Home
                      </Button>
                    </Link>
                    <Link to="/get-involved">
                      <Button variant="outline" className="w-full border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white">
                        <Heart className="w-4 h-4 mr-2" />
                        Get Involved
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </>
          )}
          {status === 'pending' && (
            <>
              <Loader2 className="w-14 h-14 text-[#9dad84] mx-auto mb-6" />
              <h1 className="font-serif text-2xl text-[#3c4a2a] mb-3">Payment Processing</h1>
              <p className="text-[#5f6d51] mb-8">
                Your payment is still being processed. You will receive an email confirmation shortly.
              </p>
              <Link to="/">
                <Button className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">Return Home</Button>
              </Link>
            </>
          )}
          {status === 'error' && (
            <>
              <AlertCircle className="w-14 h-14 text-[#d84f5b] mx-auto mb-6" />
              <h1 className="font-serif text-2xl text-[#3c4a2a] mb-3">Something Went Wrong</h1>
              <p className="text-[#5f6d51] mb-8">
                We couldn't confirm your donation. If you were charged, please contact us and we'll make it right.
              </p>
              <Link to="/donate">
                <Button className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">Try Again</Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default PaymentSuccessPage;
